import type { CopilotRunEvent } from '@customerio/hydra-copilot';

/**
 * Reads a run's stream, one JSON event per line, until the body closes. Only the text
 * after the last newline is kept between chunks, so each event is parsed once.
 */
export async function readRunStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: CopilotRunEvent) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let remainder = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      const text = done ? decoder.decode() : decoder.decode(value, { stream: true });
      const lastNewline = text.lastIndexOf('\n');

      if (done) {
        emitLines(remainder + text, onEvent);
        return;
      }

      if (lastNewline === -1) {
        remainder += text;
        continue;
      }

      emitLines(remainder + text.slice(0, lastNewline), onEvent);
      remainder = text.slice(lastNewline + 1);
    }
  } finally {
    // Releases the connection when an event handler throws mid-stream; a body that already closed has nothing to cancel.
    reader.cancel().catch((error: unknown) => {
      // oxlint-disable-next-line no-console
      console.warn('Could not cancel the run stream:', error);
    });
  }
}

function emitLines(text: string, onEvent: (event: CopilotRunEvent) => void): void {
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.length === 0) {
      continue;
    }

    // The server writes each event with the same encoder as the session log, which the
    // client already reads as typed messages; the run state ignores event types it doesn't know.
    let event: CopilotRunEvent;
    try {
      event = JSON.parse(trimmed);
    } catch {
      // oxlint-disable-next-line no-console
      console.error('Error parsing message from server:', trimmed);
      continue;
    }
    onEvent(event);
  }
}
